import type React from "react"
import { Table, Tag, Typography, type TablePaginationConfig, type TableProps } from "antd"
import { UserCellLink } from "@/components/UserCellLink"
import { DeviceBadge } from "@/components/DeviceBadge"
import { useTableScroll } from "@/hooks/useTableScroll"
import { WEB_URL } from "@/configs/config"
import dayjs from "dayjs"
import "@/styles/table.full.css"

const { Text } = Typography

export interface SearchLogItem {
  id: number
  query: string
  match_type: "exact" | "fuzzy" | "none" | "ai"
  matched_type?: string | null
  matched_id?: number | null
  matched_term?: string | null
  matched_slug?: string | null
  commit_method?: "keyboard" | "pointer" | null
  selected_index?: number | null
  user_id?: number | null
  user_agent?: string | null
  ip_address?: string | null
  latency_ms?: number | null
  created_at: string
  user?: {
    id: number
    name: string
    email: string
    username: string
    avatar?: string
    is_test?: number
  } | null
}

interface SearchLogTableProps {
  data: SearchLogItem[]
  loading: boolean
  pagination?: TableProps<SearchLogItem>["pagination"]
}

export const SearchLogTable: React.FC<SearchLogTableProps> = ({ data, loading, pagination }) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps<SearchLogItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 75,
      align: "center",
      fixed: "left",
      render: (val: number) => <span className="font-mono text-gray-500 text-xs">#{val}</span>,
    },
    {
      title: "Người dùng",
      key: "user",
      width: 200,
      fixed: "left",
      render: (_, record: SearchLogItem) => (
        <UserCellLink user={record.user} userId={record.user_id} fallback={<Tag color="default">Khách</Tag>} />
      ),
    },
    {
      title: "Từ khóa tra cứu",
      dataIndex: "query",
      key: "query",
      width: 170,
      render: (val: string) => <span className="text-gray-800">{val}</span>,
    },
    {
      title: "Từ kết quả",
      dataIndex: "matched_term",
      key: "matched_term",
      width: 170,
      render: (val: string | null | undefined, record: SearchLogItem) => {
        if (!val) return <Text type="secondary">—</Text>
        const slug = record.matched_slug || encodeURIComponent(val)
        const type = record.matched_type?.toLowerCase().replace(/_/g, "-") || ""

        let url: string | null = null
        if (type === "word-form" || type === "wordform") {
          url = null
        } else if (type === "phrase") {
          url = `${WEB_URL}/en-vi/phrases/${slug}`
        } else if (type === "collocation") {
          url = `${WEB_URL}/en-vi/collocations/${slug}`
        } else if (type === "phrasal-verb") {
          url = `${WEB_URL}/en-vi/phrasal-verbs/${slug}`
        } else if (type === "idiom") {
          url = `${WEB_URL}/en-vi/idioms/${slug}`
        } else {
          // headword, ai, and default
          url = `${WEB_URL}/en-vi/dictionary/${slug}`
        }

        if (!url) {
          return <span className="font-semibold text-gray-800">{val}</span>
        }

        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-blue-600 hover:text-blue-800 hover:underline"
          >
            {val}
          </a>
        )
      },
    },
    {
      title: "Loại kết quả",
      dataIndex: "matched_type",
      key: "matched_type",
      width: 130,
      align: "center",
      render: (val?: string | null) => {
        if (!val) return <Text type="secondary">—</Text>
        return (
          <Tag color="cyan" className="font-medium">
            {val}
          </Tag>
        )
      },
    },
    {
      title: "Loại Match",
      dataIndex: "match_type",
      key: "match_type",
      width: 120,
      align: "center",
      render: (val: string) => {
        const color = val === "exact" ? "green" : val === "fuzzy" ? "orange" : val === "ai" ? "purple" : "red"
        return <Tag color={color}>{val?.toUpperCase()}</Tag>
      },
    },
    {
      title: "Phương thức",
      dataIndex: "commit_method",
      key: "commit_method",
      width: 130,
      align: "center",
      render: (val?: string | null) => {
        if (!val) return <Text type="secondary">—</Text>
        const color = val === "keyboard" ? "blue" : "purple"
        return <Tag color={color}>{val}</Tag>
      },
    },
    {
      title: "Thiết bị",
      dataIndex: "user_agent",
      key: "user_agent",
      width: 130,
      align: "center",
      render: (ua: string | null | undefined, record: SearchLogItem) => (
        <DeviceBadge userAgent={ua} ipAddress={record.ip_address} />
      ),
    },
    {
      title: "Độ trễ",
      dataIndex: "latency_ms",
      key: "latency_ms",
      width: 90,
      align: "center",
      render: (val?: number | null) => {
        if (val === null || val === undefined) return <Text type="secondary">—</Text>
        const color = val > 1000 ? "text-red-500 font-semibold" : val > 500 ? "text-amber-500 font-medium" : "text-emerald-600"
        return <span className={`font-mono text-xs ${color}`}>{val}ms</span>
      },
    },
    {
      title: "Thời gian",
      dataIndex: "created_at",
      key: "created_at",
      width: 135,
      align: "center",
      render: (val: string) => {
        if (!val) return "—"
        const d = dayjs(val)
        return (
          <div className="flex flex-col font-mono text-xs leading-tight">
            <span className="font-medium text-gray-800">{d.format("DD/MM/YYYY")}</span>
            <Text type="secondary" className="text-[11px]">
              {d.format("HH:mm:ss")}
            </Text>
          </div>
        )
      },
    },
  ]

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table<SearchLogItem>
        bordered
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

export default SearchLogTable
